'use client';

import React, { useMemo } from 'react';
import { CircularProgress } from 'src/components/PulseUI';
import { StrategyCodesList } from 'src/components/Strategy/Center/components/StrategyCodesList';
import { useGetEngageStrategyDetailsQuery } from 'src/api/redux/strategy/engageStrategyApi';
import { PatientJourneyDetails } from 'src/components/Strategy/Profiles/components/StrategyProfileInfoDrawer/components';
import { useGetTargetStrategyDetailsQuery } from 'src/api/redux/strategy/targetStrategyApi';

interface StrategyInfoSectionProps {
  chainData: MedScout.ChainedStrategy;
  isProfileView?: boolean;
  compact?: boolean;
}

const StrategyInfoSection: React.FC<StrategyInfoSectionProps> = ({
  chainData,
  isProfileView = false,
  compact = false,
}) => {
  const targetStrategyId = useMemo(() => {
    return chainData?.target_strategy?.id?.toString() || null;
  }, [chainData]);

  const engageStrategyIds = useMemo(() => {
    if (!chainData?.engage_strategies?.length) return [];
    return chainData.engage_strategies
      .map((strategy: any) => strategy.id?.toString())
      .filter(Boolean);
  }, [chainData]);

  const { data: targetStrategyDetails, isLoading: isTargetStrategyLoading } =
    useGetTargetStrategyDetailsQuery(targetStrategyId || '', {
      skip: !targetStrategyId,
    });

  const {
    currentData: engageStrategyDetails,
    isLoading: isEngageStrategyLoading,
    isError: isEngageStrategyError,
  } = useGetEngageStrategyDetailsQuery(engageStrategyIds[0] || '', {
    skip: engageStrategyIds.length === 0,
  });

  const targetCriteriaInputs =
    targetStrategyDetails?.template?.criteria_inputs || {};
  const engageCriteriaInputs =
    engageStrategyDetails?.template?.criteria_inputs || {};

  const isLoading = isTargetStrategyLoading || isEngageStrategyLoading;

  const shouldShowEngageStrategy =
    engageStrategyIds.length > 0 &&
    !isEngageStrategyError &&
    engageStrategyDetails;

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-6"
        data-testid="strategy-info-section-loading"
        aria-label="Loading strategy details"
      >
        <CircularProgress size="sm" color="primary" />
      </div>
    );
  }

  return (
    <div
      className={compact ? 'flex flex-col gap-4' : 'flex flex-col gap-8'}
      data-testid={`strategy-info-section-${chainData.id}`}
      role="region"
      aria-label={`Strategy details for ${chainData.label}`}
    >
      {/* Full header — only in non-compact mode (compact shows label in accordion summary) */}
      {!compact && chainData.label && (
        <div className="flex flex-col gap-2">
          <h3
            className="text-xl font-bold text-primary"
            data-testid="strategy-info-section-title"
          >
            {chainData.label} Strategy
          </h3>
          {chainData.description && (
            <p
              className="text-base text-primary"
              data-testid="strategy-info-section-description"
            >
              {chainData.description}
            </p>
          )}
        </div>
      )}

      {targetStrategyDetails && (
        <div
          className={compact ? 'flex flex-col gap-1' : 'flex flex-col gap-2'}
          data-testid={`target-strategy-section-${chainData.id}`}
        >
          {targetStrategyDetails.label && (
            <p
              className={
                compact
                  ? 'text-xs font-semibold text-secondary uppercase tracking-wide'
                  : 'text-lg font-bold text-primary'
              }
              data-testid="target-strategy-label"
            >
              {targetStrategyDetails.label}
            </p>
          )}
          <StrategyCodesList
            criteriaInputs={targetCriteriaInputs}
            strategyDetails={targetStrategyDetails as any}
          />
        </div>
      )}

      {targetStrategyDetails && shouldShowEngageStrategy && (
        <div
          className="w-1/3 h-px bg-neutral-200"
          role="separator"
          aria-hidden="true"
        />
      )}

      {shouldShowEngageStrategy && (
        <div
          className={compact ? 'flex flex-col gap-1' : 'flex flex-col gap-2'}
          data-testid={`engage-strategy-section-${chainData.id}`}
        >
          {engageStrategyDetails.label && (
            <p
              className={
                compact
                  ? 'text-xs font-semibold text-secondary uppercase tracking-wide'
                  : 'text-lg font-bold text-primary'
              }
              data-testid="engage-strategy-label"
            >
              {engageStrategyDetails.label}
            </p>
          )}
          {engageStrategyDetails.type === 'PATIENT_JOURNEY' ? (
            <PatientJourneyDetails
              engageStrategyDetails={engageStrategyDetails as any}
              isProfileView={isProfileView}
            />
          ) : (
            <StrategyCodesList
              criteriaInputs={engageCriteriaInputs}
              strategyDetails={engageStrategyDetails as any}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default StrategyInfoSection;
